import {
  Box,
  Card,
  DELETE_WARNING,
  DataDisplay,
  DeleteIconButton,
  GrabIconButton,
  Grid,
  GridBreak,
  IconButton,
  List,
  RichTextEditor,
  RichTextReader,
  Stack,
  Switch,
  Tooltip,
  Typography,
  enumContains,
  pn,
} from "@compass/core";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { Input, Select } from "components";
import { MyDroppable } from "components/common/Draggable/Droppable";
import {
  Application,
  ApplicationAnswer,
  ApplicationQuestion,
  ApplicationSection,
  ApplicationSubQuestion,
  ApplicationViewResource,
} from "generated/openApi";
import produce from "immer";
import {
  BranchableFormQuestionTypes,
  FormQuestionTypes,
  FormQuestionTypesWithAnswers,
  FormQuestionTypesWithSubQuestions,
} from "pages/cobro/surveys/SurveyPage/Questions/SurveyQuestionEnum";
import { useEffect, useState } from "react";
import { DragDropContext } from "react-beautiful-dnd";
import { createEntity } from "utils/CreateEntity";
import { reorder } from "utils/Reorder";
import { ApplicationQuestionAnswer } from "./ApplicationQuestionAnswer";
import { FormQuestionWriteInTypes } from "pages/cobro/surveys/SurveyPage/Questions/SurveyQuestionEnum";
import { ApplicationQuestionSubQuestion } from "./ApplicationQuestionSubQuestion";

type ApplicationQuestionContainerProps = {
  selected?: boolean;
  setSelected: (value: ApplicationQuestion | null) => void;
  viewResource: ApplicationViewResource;
  question: ApplicationQuestion | undefined;
  application: Application;
  updateQuestion: (value: ApplicationQuestion) => void;
  addQuestion: (displayOrder: number, applicationSectionId: number) => void;
  validationErrors: Object | undefined;
  branchableQuestions: ApplicationQuestion[];
  branchableSections: ApplicationSection[];
};

export const ApplicationQuestionContainer = ({
  selected,
  setSelected,
  viewResource,
  question,
  updateQuestion,
  addQuestion,
  application,
  validationErrors,
  branchableQuestions,
  branchableSections,
}: ApplicationQuestionContainerProps) => {
  const [applicationQuestion, setApplicationQuestion] = useState<ApplicationQuestion | undefined>(question);
  const [answers, setAnswers] = useState<ApplicationAnswer[]>(question?.applicationAnswers || []);
  const [subQuestions, setSubQuestions] = useState<ApplicationSubQuestion[]>(question?.applicationSubQuestions || []);
  const [isHovered, setIsHovered] = useState<boolean>(false);

  const properties = viewResource.properties || [];

  useEffect(() => {
    setApplicationQuestion(
      produce((draft) => {
        draft!.applicationAnswers = answers!;
      })
    );
  }, [answers]);

  useEffect(() => {
    setApplicationQuestion(
      produce((draft) => {
        draft!.applicationSubQuestions = subQuestions!;
      })
    );
  }, [subQuestions]);

  useEffect(() => {
    updateQuestion(applicationQuestion!);
  }, [applicationQuestion]);

  const deleteSection = (e) => {
    if (window.confirm(DELETE_WARNING)) {
      e.stopPropagation();
      setApplicationQuestion(
        produce((draft) => {
          draft!.deleted = true;
        })
      );
    }
  };

  const addNewAnswer = () => {
    const displayOrder: number = answers?.length! + 1;

    setAnswers(
      produce((draft) => {
        let newAnswer: ApplicationAnswer = createEntity<ApplicationAnswer>({
          displayOrder,
          name: `Answer ${displayOrder}`,
        });

        draft?.push(newAnswer);
      })
    );
  };

  const addNewSubQuestion = () => {
    const displayOrder: number = subQuestions?.length! + 1;

    setSubQuestions(
      produce((draft) => {
        let newSubQuestion: ApplicationSubQuestion = createEntity<ApplicationSubQuestion>({
          displayOrder,
          name: `Sub-Question ${displayOrder}`,
          label: `${displayOrder}`,
        });

        draft?.push(newSubQuestion);
      })
    );
  };

  const updateAnswer = (answer: ApplicationAnswer) => {
    let index = answers.findIndex((a) => a.id == answer.id && a.displayOrder == answer.displayOrder);
    if (index != -1) {
      setAnswers(
        produce((draft) => {
          draft![index!] = answer;
        })
      );
    }
  };

  const updateSubQuestion = (subQuestion: ApplicationSubQuestion) => {
    let index = subQuestions.findIndex((q) => q.id == subQuestion.id && q.displayOrder == subQuestion.displayOrder);
    if (index != -1) {
      setSubQuestions(
        produce((draft) => {
          draft![index!] = subQuestion;
        })
      );
    }
  };

  const getValidationError = () => {
    var sectionIndex = application.applicationSections?.findIndex(
      (x) => x.id == applicationQuestion?.applicationSectionId
    );

    if (sectionIndex == -1 || !validationErrors) return null;

    var val = validationErrors as any;
    if (
      !val.applicationSections ||
      !val.applicationSections[sectionIndex!] ||
      !val.applicationSections[sectionIndex!].applicationQuestions ||
      !val.applicationSections[sectionIndex!].applicationQuestions[applicationQuestion?.displayOrder! - 1]
    ) {
      return null;
    }

    if (val.applicationSections[sectionIndex!].applicationQuestions[applicationQuestion?.displayOrder! - 1].name) {
      return val.applicationSections[sectionIndex!].applicationQuestions[applicationQuestion?.displayOrder! - 1].name;
    }

    if (
      val.applicationSections[sectionIndex!].applicationQuestions[applicationQuestion?.displayOrder! - 1]
        .formQuestionTypeId
    ) {
      return val.applicationSections[sectionIndex!].applicationQuestions[applicationQuestion?.displayOrder! - 1]
        .formQuestionTypeId;
    }

    if (
      val.applicationSections[sectionIndex!].applicationQuestions[applicationQuestion?.displayOrder! - 1]
        .formQuestionWriteInTypeId
    ) {
      return val.applicationSections[sectionIndex!].applicationQuestions[applicationQuestion?.displayOrder! - 1]
        .formQuestionWriteInTypeId;
    }
    return null;
  };

  const onDragEnd = (result) => {
    const { source, destination, type } = result;

    // dropped outside the list
    if (!destination) {
      return;
    }

    const sID = source.droppableId;
    const dID = destination.droppableId;

    if (type == "subQuestions") {
      // if (sID === dID) {
      //   const tempSubQuestions = reorder<FormSubQuestion>(subQuestions, source.index, destination.index);
      //   setSubQuestions(tempSubQuestions);
      // }
    } else {
      if (sID === dID) {
        const tempFormAnswers = reorder<ApplicationAnswer>(answers, source.index, destination.index);
        setAnswers(tempFormAnswers);
      }
    }
  };

  const sectionField = () => {
    return (
      <>
        {!selected && (
          <DataDisplay label="Section">
            {application?.applicationSections?.find((x) => x.id === applicationQuestion?.applicationSectionId)?.name}
          </DataDisplay>
        )}
        {selected && (
          <Select
            label="Section"
            //errors={validationErrors?.[pn<Application>("parentApplicationId")]}
            value={application?.applicationSections?.find((x) => x.id === applicationQuestion?.applicationSectionId)}
            options={application?.applicationSections}
            getOptionLabel={(option) => option.name}
            onChange={(e, value) =>
              setApplicationQuestion(
                produce((draft) => {
                  draft!.applicationSectionId = value?.id;
                })
              )
            }
          />
        )}
      </>
    );
  };

  const labelField = () => {
    return (
      <>
        {!selected && (
          <DataDisplay label="Label">
            {applicationQuestion?.label}
          </DataDisplay>
        )}
        {selected && (
          <Input
            disabled={applicationQuestion?.formQuestionTypeId == FormQuestionTypes.MatrixGrouped}
            label="Label"
            value={applicationQuestion?.label}
            onChange={(e) =>
              setApplicationQuestion(
                produce((draft) => {
                  draft!.label = e.target.value;
                })
              )
            }
          />
        )}
      </>
    );
  };

  const questionTypeField = () => {
    return (
      <>
        {!selected && (
          <DataDisplay label="Question Type">
            {viewResource?.formQuestionTypes?.find((x) => x.id === applicationQuestion?.formQuestionTypeId)?.name}
          </DataDisplay>
        )}
        {selected && (
          <Select
            label="Type"
            //errors={validationErrors?.[pn<Application>("parentApplicationId")]}
            value={viewResource?.formQuestionTypes?.find((x) => x.id === applicationQuestion?.formQuestionTypeId)}
            options={viewResource?.formQuestionTypes}
            getOptionLabel={(option) => option.name}
            onChange={(e, value) =>
              setApplicationQuestion(
                produce((draft) => {
                  draft!.formQuestionTypeId = value?.id;
                })
              )
            }
          />
        )}
      </>
    );
  };

  const propertyField = () => {
    return (
      <>
        {!selected && (
          <DataDisplay label="Property">
            {
              viewResource?.applicationPropertyTypes?.find((x) => x.id === applicationQuestion?.applicationPropertyId)
                ?.name
            }
          </DataDisplay>
        )}
        {selected && (
          <Select
            label="Property"
            //errors={validationErrors?.[pn<Application>("parentApplicationId")]}
            value={viewResource?.applicationPropertyTypes?.find(
              (x) => x.id === applicationQuestion?.applicationPropertyId
            )}
            options={viewResource?.applicationPropertyTypes}
            getOptionLabel={(option) => option.name}
            onChange={(e, value) =>
              setApplicationQuestion(
                produce((draft) => {
                  draft!.applicationPropertyId = value?.id;
                })
              )
            }
          />
        )}
      </>
    );
  };

  const questionSizeField = () => {
    return (
      <>
        {!selected && (
          <DataDisplay label="Question Size">
            {applicationQuestion?.questionWidth == "dl-M"
              ? "1/3 Row"
              : applicationQuestion?.questionWidth == "dl-L"
                ? "1/2 Row"
                : "Full Row"}
          </DataDisplay>
        )}
        {selected && (
          <Select
            label="Question Size"
            value={
              applicationQuestion?.questionWidth == "dl-M"
                ? "1/3 Row"
                : applicationQuestion?.questionWidth == "dl-L"
                  ? "1/2 Row"
                  : "Full Row"
            }
            options={viewResource?.questionSizes}
            getOptionLabel={(option) => option}
            onChange={(e, value) => {
              setApplicationQuestion(
                produce((draft) => {
                  draft!.questionWidth = value == "1/3 Row" ? "dl-M" : value == "1/2 Row" ? "dl-L" : "dl-full";
                })
              );
            }}
          />
        )}
      </>
    );
  };

  const openEndedTypeField = () => {
    return (
      <>
        {!selected && (
          <DataDisplay label="Open-Ended Type">
            {
              viewResource?.formQuestionWriteInTypes?.find(
                (x) => x.id === applicationQuestion?.formQuestionWriteInTypeId
              )?.name
            }
          </DataDisplay>
        )}
        {selected && (
          <Select
            label="Open-Ended Type"
            //errors={validationErrors?.[pn<Application>("parentApplicationId")]}
            value={viewResource?.formQuestionWriteInTypes?.find(
              (x) => x.id === applicationQuestion?.formQuestionWriteInTypeId
            )}
            options={viewResource?.formQuestionWriteInTypes}
            getOptionLabel={(option) => option.name}
            onChange={(e, value) =>
              setApplicationQuestion(
                produce((draft) => {
                  draft!.formQuestionWriteInTypeId = value?.id;
                })
              )
            }
          />
        )}
      </>
    );
  };

  const questionField = () => {
    return (
      <>
        {!selected && <RichTextReader label="Question" html={applicationQuestion?.name || ""} />}
        {selected && (
          <RichTextEditor
            label="Question"
            placeholder="Enter question..."
            value={applicationQuestion?.name}
            onChange={(value) =>
              setApplicationQuestion(
                produce((draft) => {
                  draft!.name = value!;
                })
              )
            }
          />
        )}
      </>
    );
  };

  const subQuestionField = () => {
    return (
      <>
        {subQuestions &&
          applicationQuestion &&
          enumContains(applicationQuestion?.formQuestionTypeId, FormQuestionTypesWithSubQuestions) && (
            <DataDisplay label="Sub-Questions">
              <List>
                <MyDroppable
                  id={`subQuestion:${applicationQuestion!.id}:${applicationQuestion!.displayOrder}`}
                  type="subQuestions"
                >
                  {subQuestions
                    ?.filter((a) => !a.deleted)
                    .map((a, index) => {
                      return (
                        <ApplicationQuestionSubQuestion
                          updateSubQuestion={updateSubQuestion}
                          key={`${a.id}:${a.displayOrder}`}
                          index={index}
                          selected={selected}
                          subQuestion={a}
                        />
                      );
                    })}

                  {selected && (
                    <Box px={20}>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          addNewSubQuestion();
                        }}
                      >
                        <Tooltip title="Add New Sub Question">
                          <AddCircleOutlineIcon />
                        </Tooltip>
                      </IconButton>
                    </Box>
                  )}
                </MyDroppable>
              </List>
            </DataDisplay>
          )}
      </>
    );
  };

  const defaultAnswerField = () => {
    return (
      <Input
        label="Default Answer"
        value={applicationQuestion?.defaultAnswer}
        onChange={(e) =>
          setApplicationQuestion(
            produce((draft) => {
              draft!.defaultAnswer = e.target.value;
            })
          )
        }
      />
    );
  };

  const isMandatoryField = () => {
    return (
      <>
        {selected && (
          <Switch
            label="Required"
            onClick={() => {
              setApplicationQuestion(
                produce((draft) => {
                  draft!.isMandatory = !applicationQuestion?.isMandatory;
                })
              );
            }}
            checked={applicationQuestion?.isMandatory}
          ></Switch>
        )}
      </>
    );
  };

  const markedForReviewField = () => {
    return (
      <>
        {selected && (
          <Tooltip title="Indicates this question should be filled out by a reviewer.">
            <Switch
              label="Flag for Review"
              onClick={() => {
                setApplicationQuestion(
                  produce((draft) => {
                    draft!.markedForReview = !applicationQuestion?.markedForReview;
                  })
                );
              }}
              checked={applicationQuestion?.markedForReview}
            ></Switch>
          </Tooltip>
        )}
      </>
    );
  };

  const reviewerEmailField = () => {
    return (
      <>
        {selected && (
          <Tooltip title="Indicates this email is the reviewer's email address. An email will be sent to this location for review upon application completion.">
            <Switch
              label="Is Reviewer's Email"
              onClick={() => {
                setApplicationQuestion(
                  produce((draft) => {
                    draft!.isReviewerEmail = !applicationQuestion?.isReviewerEmail;
                  })
                );
              }}
              checked={applicationQuestion?.isReviewerEmail}
            ></Switch>
          </Tooltip>
        )}
      </>
    );
  };

  const answerField = () => {
    return (
      <DragDropContext onDragEnd={onDragEnd}>
        {enumContains(applicationQuestion?.formQuestionTypeId, FormQuestionTypesWithAnswers) && (
          <DataDisplay label="Answers">
            <List>
              <MyDroppable
                id={`formAnswers:${applicationQuestion!.id}:${applicationQuestion!.displayOrder}`}
                type="formAnswers"
              >
                {answers
                  ?.filter((a) => !a.deleted)
                  .map((a, index) => {
                    return (
                      <ApplicationQuestionAnswer
                        key={`${a.id}:${a.displayOrder}`}
                        index={index}
                        selected={selected}
                        updateAnswer={updateAnswer}
                        answer={a}
                        viewResource={viewResource}
                        branchableQuestions={branchableQuestions}
                        isBranchable={enumContains(
                          applicationQuestion?.formQuestionTypeId,
                          BranchableFormQuestionTypes
                        )}
                      />
                    );
                  })}
              </MyDroppable>

              {selected && (
                <Box px={20}>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      addNewAnswer();
                    }}
                  >
                    <Tooltip title="Add New Answer">
                      <AddCircleOutlineIcon />
                    </Tooltip>
                  </IconButton>
                </Box>
              )}
            </List>
          </DataDisplay>
        )}
      </DragDropContext>
    );
  };

  return (
    <>
      <Grid item xs={10}>
        <Card
          onClick={() => setSelected(applicationQuestion!)}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <Grid container spacing="sm">
            <Grid item xs={12}>
              <Typography color="error">{getValidationError()}</Typography>
            </Grid>
            <Grid item xs={11}>
              <Grid container spacing="sm">
                <Grid item md={1} xs={6}>
                  {labelField()}
                </Grid>

                <Grid item md={4} xs={6}>
                  {sectionField()}
                </Grid>

                <Grid item md={4} xs={6}>
                  {questionTypeField()}
                </Grid>

                {applicationQuestion?.formQuestionTypeId == FormQuestionTypes.OpenEnded && (
                  <Grid item md={3} xs={6}>
                    {openEndedTypeField()}
                  </Grid>
                )}

                <GridBreak />

                <Grid item md={4} xs={6}>
                  {propertyField()}
                </Grid>

                <Grid item md={4} xs={6}>
                  {questionSizeField()}
                </Grid>

                {selected && applicationQuestion?.formQuestionTypeId == FormQuestionTypes.OpenEnded && (
                  <Grid item md={4} xs={6}>
                    {defaultAnswerField()}
                  </Grid>
                )}

                <GridBreak />

                <Grid item md={10} xs={12}>
                  {questionField()}
                </Grid>

                <GridBreak />

                <Grid item md={8} xs={12}>
                  {subQuestionField()}
                </Grid>

                <GridBreak />

                <Grid item md={11} xs={12}>
                  {answerField()}
                </Grid>

                <GridBreak />

                <Grid item md={3} xs={4}>
                  {isMandatoryField()}
                </Grid>

                <Grid item md={3} xs={4}>
                  {markedForReviewField()}
                </Grid>

                {question?.formQuestionWriteInTypeId == FormQuestionWriteInTypes.Email && (
                  <Grid item md={3} xs={4}>
                    {reviewerEmailField()}
                  </Grid>
                )}

                <Box />
              </Grid>
            </Grid>

            <Grid item xs={1} display="flex" direction="column" alignItems="center" justifyContent="space-between">
              <Box>
                {selected && (
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelected(null);
                    }}
                  >
                    <ExpandLessIcon />
                  </IconButton>
                )}
              </Box>
              <Box>{isHovered && <GrabIconButton />}</Box>
              <Box />
            </Grid>
          </Grid>
        </Card>
      </Grid>

      {selected && (
        <Grid item xs={2} display="flex" alignItems="center" justifyContent="center">
          <Card sx={{ maxWidth: "100px" }}>
            <Stack direction="column" spacing="md">
              <IconButton
                onClick={() =>
                  addQuestion(applicationQuestion?.displayOrder! + 1, applicationQuestion?.applicationSectionId!)
                }
              >
                <Tooltip title="Add New Question">
                  <AddCircleOutlineIcon />
                </Tooltip>
              </IconButton>

              <DeleteIconButton
                onClick={(e) => {
                  deleteSection(e);
                }}
              />
            </Stack>
          </Card>
        </Grid>
      )}
    </>
  );
};
